import { filter, takeUntil } from 'rxjs';
import { Injectable, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Actions } from '@ngrx/effects';
import { environment } from '../../environments/environment';
import { AuthService } from '../services/auth.service';
import { selectAuthUser } from '../store/selectors/auth.selectors';
import { selectCarState } from '../store/selectors/car.selectors';
import { selectRealEstateState } from '../store/selectors/real-estate.selectors';
import * as Cars from '../store/actions/car.actions';
import * as Properties from '../store/actions/real-estate.actions';
import * as Auth from '../store/actions/auth.actions';
import { CreateSavedSearch, SavedSearch, SavedSearchPage, SavedSearchDomain, SavedGeography, criteriaSnapshot, rectangle } from './saved-search.model';

@Injectable({ providedIn: 'root' })
export class SavedSearchService {
  private readonly http = inject(HttpClient);
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly auth = inject(AuthService);
  private readonly actions = inject(Actions);
  private readonly destroy = inject(DestroyRef);
  private readonly sessionEnded = this.actions.pipe(filter(action => ([Auth.logout.type, Auth.clearAuthUser.type, Auth.clearSessionState.type] as string[]).includes(action.type)));
  readonly user = this.store.selectSignal(selectAuthUser);
  private readonly cars = this.store.selectSignal(selectCarState);
  private readonly properties = this.store.selectSignal(selectRealEstateState);
  private readonly url = environment.apiUrl + '/api/saved-searches';
  authorize(): boolean {
    if (!this.user()) {
      this.auth.redirectUrl = this.router.url;
      void this.router.navigate(['/login']);
      return false;
    }
    return this.user()?.status === 'APPROVED';
  }
  snapshot(domain: SavedSearchDomain, form: Record<string, unknown>): CreateSavedSearch {
    const state = domain === 'CAR' ? this.cars() : this.properties();
    const isMap = domain === 'CAR' ? this.cars().isCarMapView : this.properties().isRealEstateMapView;
    const criteria = isMap ? state.mapDraft ?? state.mapQuery ?? form : state.listDraft ?? state.query ?? form;
    const geography = !isMap ? {} : state.activePolygon?.length ? {polygonFilter: state.activePolygon} : rectangle(state.mapQuery);
    return {domain, criteria: criteriaSnapshot(domain, criteria as unknown as Record<string, unknown>, geography)};
  }
  create(request: CreateSavedSearch) { return this.http.post<SavedSearch>(this.url, request, { observe: 'response' }).pipe(takeUntil(this.sessionEnded)); }
  list(offset = 0) { return this.http.get<SavedSearchPage>(this.url, { params: { limit: 50, offset } }).pipe(takeUntil(this.sessionEnded)); }
  unsubscribe(id: number) { return this.http.delete<void>(this.url + '/' + id).pipe(takeUntil(this.sessionEnded)); }
  unsubscribeByToken(token: string) { return this.http.post<void>(this.url + '/unsubscribe', { token }); }
  errorKey(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      if (error.status === 401) return 'SAVED_SEARCH.signIn';
      if (error.status === 403) return 'SAVED_SEARCH.eligible';
      if (error.status === 400 || error.status === 413) return 'SAVED_SEARCH.invalid';
    }
    return 'SAVED_SEARCH.error';
  }
}
